import { Icon } from "@vds/icons";
import { Body, Title } from "@vds/typography";
import { useEffect, useRef, useState } from "react";
import { Image, Layer, Line, Stage } from "react-konva";
import useImage from "use-image";
import userIcon, { ReactComponent as UserIcon } from "../../assets/virtual-target.svg";
import { CALIBRATION_DEFAULTS, CAMERA_ONBOARDING_TEST_IDS } from "./cameraOnboardingConstants";

const ManualCalibration = ({
  setFarPosition,
  setFarSize,
  setNearSize,
  imageUrl,
  deviceid,
  farSize,
  nearSize,
  farposition,
  pageName,
  existingCalibrationConfig,
  resizeOnWindowChange = false,
}) => {
  const imageRef = useRef(null); // Ref for the image
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const imageRef1 = useRef(null);
  const [smallImage] = useImage(userIcon);
  // let imageUrl = resolution4;
  const images = Array.isArray(imageUrl)
    ? imageUrl.filter((f) => f.id === deviceid)
    : [];
  const [image] = useImage(
    Array.isArray(imageUrl) ? images[0]?.imageurl : imageUrl
  );
  const [showIcon, setShowIcon] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [canvasHeight, setCanvasHeight] = useState(0);

  const initialFarPos = existingCalibrationConfig?.FarPosition
    ? existingCalibrationConfig.FarPosition / 100
    : CALIBRATION_DEFAULTS.FAR_POSITION;
  const initialNear = existingCalibrationConfig?.SceneParameters?.near_size
    ? existingCalibrationConfig.SceneParameters.near_size / 100
    : CALIBRATION_DEFAULTS.NEAR_SIZE;
  const initialFar = existingCalibrationConfig?.SceneParameters?.far_size
    ? existingCalibrationConfig.SceneParameters.far_size / 100
    : CALIBRATION_DEFAULTS.FAR_SIZE;

  const [farPos, setFarPos] = useState(initialFarPos);
  const [near, setNear] = useState(initialNear);
  const [far, setFar] = useState(initialFar);
  const [iconPosition, setIconPosition] = useState({ x: 0, y: 0 });

  const mannequinAspectRatio = 51 / 172;

  // let pageName = "pairingFlow";

  const handleParametersInitialisation = () => {
    setNearSize([{ nearSize: parseInt(near * 100), id: deviceid }]);
    setFarSize([{ farSize: parseInt(far * 100), id: deviceid }]);
    setFarPosition([{ farposition: parseInt(farPos * 100), id: deviceid }]);
  }

  const handleResetNearAndFar = () => {
    setNear(initialNear);
    setFar(initialFar);
    setFarPos(initialFarPos);
  };

  useEffect(() => {
    const image1 = imageRef1.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    handleParametersInitialisation();
    image1.onload = () => {
      canvas.width = image1.width;
      canvas.height = image1.height;
      context.drawImage(image1, 0, 0, image1.width, image1.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const modalSize = window.innerWidth * 0.5;
        const maxWidth = modalSize * 1;
        const maxHeight = window.innerHeight * 0.5; // Set your desired max height
        const ratio = imageData.height / imageData.width;

        let displayWidth = imageData.width;
        let displayHeight = imageData.height;

        // Calculate scaling factors for both dimensions
        const widthScale = maxWidth / imageData.width;
        const heightScale = maxHeight / imageData.height;
        
        // Use the smaller scale factor to ensure both constraints are met
        const scale = Math.min(widthScale, heightScale, 1); // Don't scale up beyond original size
        
        if (scale < 1) {
          displayWidth = imageData.width * scale;
          displayHeight = imageData.height * scale;
        }
        setCanvasWidth(displayWidth);
        setCanvasHeight(displayHeight);
      }
  }, [imageRef1]);

  useEffect(() => {
    if (!resizeOnWindowChange) return undefined;
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas || !canvas.width || !canvas.height) return;
      const scale = Math.min(
        (window.innerWidth * 0.5) / canvas.width,
        (window.innerHeight * 0.5) / canvas.height,
        1
      );
      setCanvasWidth(canvas.width * scale);
      setCanvasHeight(canvas.height * scale);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [resizeOnWindowChange]);


  const generateLines = () => {
    const horizontalDivs = 5;
    const verticalDivs = 6;
    const scaledHeight = (farPos / 100) * canvasHeight;
    const yStart = canvasHeight - scaledHeight;
    const lines = [];

    for (let i = 0; i <= horizontalDivs; i++) {
      const y = yStart + (i * scaledHeight) / horizontalDivs;
      lines.push(
        <Line
          key={`h-${i}`}
          points={[0, y, canvasWidth, y]}
          stroke={y % 4 === 0 ? "red" : "green"}
          strokeWidth={2}
        />
      );
    }

    for (let j = 1; j <= verticalDivs; j++) {
      const x = (j * canvasWidth) / verticalDivs;
      lines.push(
        <Line
          key={`v-${j}`}
          points={[x, yStart, x, canvasHeight]}
          stroke={x === canvasWidth / 2 ? "red" : "green"}
          strokeWidth={2}
        />
      );
    }
    return lines;
  };

  const handleMouseMove = (e) => {
    if (!stageRef.current) return;
    const stage = e.target.getStage();
    const p = stage.getPointerPosition();
    if (!p) return;

    const imageHeight = stage.height();

    const nearSizeH = (near / 100) * imageHeight;
    const farSizeH = (far / 100) * imageHeight;
    const posLimit = (farPos / 100) * imageHeight;

    const distFromBottom = Math.max(
      0,
      Math.min(imageHeight, imageHeight - p.y)
    );

    let iconHeight;

    if (distFromBottom <= posLimit) {
      const t = distFromBottom / posLimit;
      iconHeight = nearSizeH + (farSizeH - nearSizeH) * t;
    } else {
      iconHeight = farSizeH;
    }

    const iconWidth = iconHeight * mannequinAspectRatio;

    setIconPosition({
      x: p.x - iconWidth / 2,
      y: p.y - iconHeight,
      mannequinHeight: iconHeight,
      mannequinWidth: iconWidth,
    });
  };

  const handleNearSizeChange = (value) => {
    if (value && value <= 100 && value !== "NaN") {
      setNear(parseInt(value) < 0 ? 0 : parseInt(value));
      let newNearSize = nearSize.filter((f) => f.id !== deviceid) || [];
      newNearSize.push({ nearSize: parseInt(value * 100), id: deviceid });
      setNearSize(newNearSize);
    }
  };

  const handleFarSizeChange = (value) => {
    if (value <= 100 && value !== "NaN" && value) {
      setFar(parseInt(value) < 0 ? 0 : parseInt(value));
      let newFarSize = farSize.filter((f) => f.id !== deviceid) || [];
      newFarSize.push({ farSize: parseInt(value * 100), id: deviceid });
      setFarSize(newFarSize);
    }
  };

  const handleFarPositionChange = (value) => {
    if (value <= 100 && value !== "NaN" && value) {
      setFarPos(parseInt(value) < 0 ? 0 : parseInt(value));
      const val = parseInt(value);
      if (val > 1) {
        let newFarPosition = farposition.filter((f) => f.id !== deviceid);
        newFarPosition.push({ farposition: parseInt(val * 100), id: deviceid });
        setFarPosition(newFarPosition);
      }
    }
  };

  return (
    <>
      {/* {selectName && (
        <div style={{ paddingBottom: "10px" }}>
          <Title size="small">{selectName}</Title>
        </div>
      )} */}
      <div>
        <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        <img
          ref={imageRef1}
          src={Array.isArray(imageUrl) ? images[0]?.imageurl : imageUrl}
          alt="source"
          style={{ display: "none" }}
        ></img>
      </div>
      <div
        style={{
          backgroundColor: "#000",
          // width: `${canvasWidth + 100}px`,
          // height: `${canvasHeight + 400}px`,
          // width: "100%",
          // height: "781px",
          borderRadius: "5px",
          margin: "0 auto",
        }}
      >
        {/* Konva Stage for rendering the image and lines */}
        
          <div style={{ padding: "15px" }}>
            <Title
              color="#ffffff"
              size="large"
              bold
              viewport="desktop"
              primitive="span"
            >
              Set scene parameters to help ensure accuracy
            </Title>
          </div>
        

        <div style={{ padding: "5px 40px" }}>
          <div style={{display: "flex", justifyContent: "center"}}>
            <Stage
              width={canvasWidth}
              height={canvasHeight}
              ref={stageRef}
              onMouseEnter={() => setShowIcon(true)}
              onMouseLeave={() => setShowIcon(false)}
              onMouseMove={(e) => handleMouseMove(e)}
              // onMouseLeave={handleMouseLeave}
            >
              <Layer>
                {/* Image with dynamic rotation */}
                <Image
                  image={image}
                  ref={imageRef}
                  x={0}
                  y={0}
                  width={canvasWidth}
                  height={canvasHeight}
                  //  rotation={rotation} // Image rotation controlled by slider
                />
                {generateLines()}

                {showIcon && (
                  <Image
                    image={smallImage}
                    x={iconPosition.x}
                    y={iconPosition.y}
                    width={iconPosition.mannequinWidth}
                    height={iconPosition.mannequinHeight}
                    ref={imageRef}
                  />
                )}
              </Layer>
            </Stage>

          </div>
            
          <div
            style={{
              display: "flex",
              marginTop: "15px",
              marginLeft: "50px"
            }}
          >
            <div
              className="col-xl-1"
              style={{
                width: "auto",
                padding: "22px 20px 0 0",
                borderRight: "1px solid #FFFFFF",
                flex: 6,
              }}
            >
              <Body
                color="#ffffff"
                size="large"
                viewport="desktop"
                primitive="span"
              >
                Parameters
              </Body>
              <div
                class="input-group"
                style={{
                  flexWrap: "nowrap",
                  gap: "32px",
                }}
              >
                <div style={{ marginTop: "20px" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <Body
                      color="#ffffff"
                      size="small"
                      viewport="desktop"
                      primitive="span"
                    >
                      Near size
                    </Body>
                    <Icon size="small" name="info" color="#FFFFFF" />
                  </div>
                  <div
                    style={{
                      border: "1px solid #fff",
                      fontSize: "14px",
                      gap: "3",
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "5px",
                      padding: "10px",
                      color: "#fff",
                      borderRadius: "20px",
                      width: "135px",
                      height: "44px",
                    }}
                  >
                    <span onClick={() => handleNearSizeChange(near - 1)} 
                    data-testid={CAMERA_ONBOARDING_TEST_IDS.NEAR_SIZE_MINUS_BUTTON}>
                      <Icon name="minus" surface="dark" size="small" />
                    </span>
                    <span style={{ display: "flex" }}>
                      {/* {dnum}% */}
                      <input
                        type="text"
                        value={near}
                        class="form-control"
                        style={{
                          color: "#fff",
                          width: "50px",
                          height: "20px",
                          border: "0px solid #000",
                          backgroundColor: "#000",
                        }}
                        data-testid={CAMERA_ONBOARDING_TEST_IDS.NEAR_SIZE_INPUT}
                        onChange={(e) => handleNearSizeChange(e.target.value)}
                      />
                      <span>%</span>
                    </span>
                    <span onClick={() => handleNearSizeChange(near + 1)}
                      data-testid={CAMERA_ONBOARDING_TEST_IDS.NEAR_SIZE_PLUS_BUTTON}>
                      <Icon name="plus" surface="dark" size="small" />
                    </span>
                  </div>
                </div>
                <div style={{ marginTop: "20px" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <Body
                      color="#ffffff"
                      size="small"
                      viewport="desktop"
                      primitive="span"
                    >
                      Far size
                    </Body>
                    <Icon size="small" name="info" color="#FFFFFF" />
                  </div>
                  <div
                    style={{
                      border: "1px solid #fff",
                      fontSize: "14px",
                      gap: "3",
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "5px",
                      padding: "10px",
                      color: "#fff",
                      borderRadius: "20px",
                      width: "135px",
                      height: "44px",
                    }}
                  >
                    <span onClick={() => handleFarSizeChange(far - 1)} 
                    data-testid={CAMERA_ONBOARDING_TEST_IDS.FAR_SIZE_MINUS_BUTTON}>
                      <Icon name="minus" surface="dark" size="small" />
                    </span>
                    <span style={{ display: "flex" }}>
                      <input
                        type="text"
                        value={far}
                        class="form-control"
                        style={{
                          color: "#fff",
                          width: "50px",
                          height: "20px",
                          border: "0px solid #000",
                          backgroundColor: "#000",
                        }}
                        data-testid={CAMERA_ONBOARDING_TEST_IDS.FAR_SIZE_INPUT}
                        onChange={(e) => handleFarSizeChange(e.target.value)}
                      />
                      <span>%</span>
                    </span>
                    <span onClick={() => handleFarSizeChange(far + 1)} 
                      data-testid={CAMERA_ONBOARDING_TEST_IDS.FAR_SIZE_PLUS_BUTTON}>
                      <Icon name="plus" surface="dark" size="small" />
                    </span>
                  </div>
                </div>
                <div style={{ marginTop: "20px" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <Body
                      color="#ffffff"
                      size="small"
                      viewport="desktop"
                      primitive="span"
                    >
                      Far position
                    </Body>
                    <Icon size="small" name="info" color="#FFFFFF" />
                  </div>
                  <div
                    style={{
                      border: "1px solid #fff",
                      fontSize: "14px",
                      gap: "3",
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "5px",
                      padding: "10px",
                      color: "#fff",
                      borderRadius: "20px",
                      width: "135px",
                      height: "44px",
                    }}
                  >
                    <span onClick={() => handleFarPositionChange(farPos - 1)} 
                      data-testid={CAMERA_ONBOARDING_TEST_IDS.FAR_POSITION_MINUS_BUTTON} >
                      <Icon name="minus" surface="dark" size="small" />
                    </span>
                    {/* <span>{num} % </span> */}
                    <span style={{ display: "flex" }}>
                      <input
                        type="text"
                        value={farPos}
                        class="form-control"
                        style={{
                          color: "#fff",
                          width: "50px",
                          height: "20px",
                          border: "0px solid #000",
                          backgroundColor: "#000",
                        }}
                        // onMouseLeave={handleMouseOut}
                        // onMouseOut={handleMouseOut}
                        data-testid={CAMERA_ONBOARDING_TEST_IDS.FAR_POSITION_INPUT}
                        onChange={(e) =>
                          handleFarPositionChange(e.target.value)
                        }
                      />
                      <span>%</span>
                    </span>
                    <span onClick={() => handleFarPositionChange(farPos + 1)} 
                      data-testid={CAMERA_ONBOARDING_TEST_IDS.FAR_POSITION_PLUS_BUTTON} >
                      <Icon name="plus" surface="dark" size="small" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ margin: "12px auto", flex: 4 }}>
              <div
                style={{
                  padding: "22px 0 10px 0",
                  marginLeft: "12px",
                  gap: "5px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Body
                  color="#ffffff"
                  size="large"
                  viewport="desktop"
                  primitive="span"
                >
                  Virtual target
                </Body>
                <div style={{ margin: "-1px 0 0 2px" }}>
                  <Icon size="medium" name="info" color="#FFFFFF" />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <UserIcon width="23px" height="83px" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManualCalibration;
